import { FC } from "react";
import {
  Avatar,
  Box,
  Palette,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ConnectionStatus } from "@/models/ticker";
import { useGameDataContext } from "@context/GameDataContext";
import { SportsVolleyball } from "@mui/icons-material";
import TeamLineup from "../../TeamLineup";
import { Player } from "@/models/gameGeneral";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";

const PlayerAvatar: FC<{
  player?: Player;
  position: "I" | "II" | "III" | "IV" | "V" | "VI";
  connectionStatus: ConnectionStatus;
  palette: Palette;
}> = ({ player, position, connectionStatus, palette }) => {
  const bgcolor = (connectionStatus: ConnectionStatus) => {
    switch (connectionStatus) {
      case "lost":
        return palette.primary.dark;
      case "loosing":
        return palette.primary.main;
      case "connected":
        return palette.primary.light;
    }
  };

  return (
    <Tooltip title={player ? `${player.lastName}` : ""}>
      <Avatar
        variant={"square"}
        sx={{
          bgcolor: bgcolor(connectionStatus),
          transition: "background-color 5s ease",
          color: palette.primary.contrastText,
        }}
      >
        {player?.jerseyNumber || position}
      </Avatar>
    </Tooltip>
  );
};

const FooterTeamLineup: FC<{ side: "left" | "right"; collapsed: boolean }> = ({
  side,
  collapsed,
}) => {
  const { game } = useGameDataContext();
  const { palette } = useTheme();

  const serving = gameState("serving");
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());

  const team = side === "left" ? leftTeam : rightTeam;
  const teamData = game?.gameData?.[team] || {
    shortName: team === leftTeam ? "A" : "B",
    clubCode: team === leftTeam ? "A" : "B",
    iconUrl: null,
  };

  const isInverted = side === "right";

  const courtLineBorderStyle = `2px solid ${palette.text.primary}`;

  return (
    <Box
      flexDirection={isInverted ? "row" : "row-reverse"}
      gap={1}
      display={"flex"}
      alignItems={"center"}
    >
      <Box
        display="flex"
        alignItems={"center"}
        flexDirection={
          collapsed ? (isInverted ? "row" : "row-reverse") : "column"
        }
        gap={collapsed ? 2 : 0}
      >
        {teamData.iconUrl ? <Avatar src={teamData.iconUrl} /> : null}
        <Typography>{teamData.clubCode}</Typography>
      </Box>
      <Box
        sx={{
          display: collapsed ? "none" : "flex",
          alignItems: "center",
          height: collapsed ? "auto" : "190px",
          ...(isInverted
            ? { borderLeft: courtLineBorderStyle }
            : { borderRight: courtLineBorderStyle }),
        }}
      >
        <TeamLineup
          side={side}
          sx={{ height: "90%", aspectRatio: "2/3" }}
          fontSize={"20px"}
        />
      </Box>
      <Box
        display={"flex"}
        height={"80%"}
        alignSelf={
          collapsed ? "center" : isInverted ? "flex-start" : "flex-end"
        }
        py={"12px"}
      >
        <SportsVolleyball opacity={serving === team ? 1 : 0} />
      </Box>
    </Box>
  );
};

export default FooterTeamLineup;
